import { useEffect, useRef, useState } from "react";
import { Copy, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";

export default function AdminMedia() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("image", file);
        fd.append("label", file.name);
        await api.post("/admin/upload", fd);
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error((Array.isArray(detail) ? detail[0]?.msg : detail) || `Could not upload ${file.name}`);
      }
    }
    setUploading(false);
    load();
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.label || m.path}"? Sections or products using it will lose this image.`)) return;
    await api.delete(`/admin/media/${m.id}`);
    setItems((list) => list.filter((x) => x.id !== m.id));
  };

  const copy = (path) => {
    navigator.clipboard?.writeText(path);
    toast.success("Path copied — paste it into any image field.");
  };

  return (
    <section data-testid="admin-media">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-[#5C5C5C]">Upload images and videos, then pick them anywhere in the CMS.</p>
        <div>
          <button
            data-testid="media-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
          </button>
          <input ref={fileRef} data-testid="media-file-input" type="file" multiple accept="image/jpeg,image/png,image/webp,video/mp4,video/webm" onChange={upload} className="hidden" />
        </div>
      </div>

      {!items ? (
        <div className="flex justify-center py-20"><Loader2 size={26} className="animate-spin text-rose" /></div>
      ) : items.length === 0 ? (
        <p data-testid="media-empty" className="rounded-2xl border border-line bg-white py-16 text-center text-sm text-[#5C5C5C]">
          No media yet. Upload product photos, banners or videos here.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id}`} className="overflow-hidden rounded-2xl border border-line bg-white">
              {m.kind === "video" ? (
                <video src={imgUrl(m.path)} className="aspect-square w-full object-cover" muted controls preload="metadata" />
              ) : (
                <img src={imgUrl(m.path)} alt={m.label} className="aspect-square w-full object-cover" loading="lazy" />
              )}
              <div className="flex items-center justify-between gap-1 p-3">
                <span className="truncate text-[11px] text-[#5C5C5C]">{m.label || m.path}</span>
                <div className="flex shrink-0 gap-1">
                  <button data-testid={`media-copy-${m.id}`} onClick={() => copy(m.path)} aria-label="Copy path" className="flex h-8 w-8 items-center justify-center rounded-full border border-line transition-colors hover:border-rose hover:text-rose">
                    <Copy size={13} />
                  </button>
                  <button data-testid={`media-delete-${m.id}`} onClick={() => remove(m)} aria-label="Delete media" className="flex h-8 w-8 items-center justify-center rounded-full border border-line transition-colors hover:border-red-400 hover:text-red-500">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
